import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";

import LoadingButton from "_shared/_views/loading/loading-button";
import PlaceAutocomplete from "_shared/addresses/_views/place-autocomplete";
import Address from "_shared/addresses/address";
import Guid from "_shared/guids/guid";
import axios, { HttpStatusCode } from "axios";
import EntityAgreementAPIService, {
  InvalidEntityAgreementError,
} from "legal-entities/agreements/_api/entity-agreement-api-service";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import EntityNameInput from "legal-entities/entity-names/_views/inputs/entity-name-input";
import EntityName from "legal-entities/entity-names/entity-name";
import { useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import Stack from "@mui/material/Stack";

const PageContainer = styled(Box)(({ theme }) => ({
  height: "100%",
  overflow: "auto",
  padding: theme.spacing(4, 2),
  textAlign: "center",
}));
const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));
const LegalEntityLocationInput = styled(PlaceAutocomplete)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  width: "100%",
  maxWidth: "400px",
  margin: "0 auto",
}));
const AcceptButton = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
const HelperText = styled(Container)(({ theme }) => ({
  color: theme.palette.text.secondary,
  margin: theme.spacing(2, "auto"),
  padding: theme.spacing(2),
}));

export default function EntityAgreementPage() {
  const [originalAgreement, setOriginalAgreement] = useState<EntityAgreement>();
  const [formEntityName, setFormEntityName] = useState<EntityName>();
  const [formLocation, setFormLocation] = useState<Address>();
  const [saving, setSaving] = useState<boolean>(false);

  const session = useSession();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const locationServicesRef = useRef<boolean>(false);

  const initAgreement = useCallback((agreement: EntityAgreement) => {
    if (!agreement) throw new Error("Can't init empty agreement");
    setOriginalAgreement(agreement);
    setFormEntityName(agreement.entityName);
    setFormLocation(agreement.entityLocation);
  }, []);

  useEffect(() => {
    async function loadAgreement(abortController: AbortController): Promise<EntityAgreement> {
      let agreement: EntityAgreement;
      const sessionAgreement = session.openEntityAgreements.find(
        (agreement: EntityAgreement) => agreement.id.value === params.id,
      );
      if (sessionAgreement) return sessionAgreement;
      if (!params.id) throw new Error("No agreement ID provided");
      const service = new EntityAgreementAPIService(session);
      agreement = await service.getEntityAgreementById(new Guid(params.id), abortController);
      return agreement;
    }
    const abortController = new AbortController();
    loadAgreement(abortController)
      .then((agreement: EntityAgreement) => initAgreement(agreement))
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.error(error);
        enqueueSnackbar("Entity agreement is currently unavailable", { variant: "error" });
      });
    return () => abortController.abort();
  }, [initAgreement, params.id, session, session.openEntityAgreements.length]);

  async function handleAcceptAgreement() {
    setSaving(true);

    try {
      if (!originalAgreement) throw new Error("Original agreement is missing");
      if (!validEntityAgreement) throw new InvalidEntityAgreementError("Agreement is invalid");
      const service = new EntityAgreementAPIService(session);
      const finalAgreement = await service.updateEntityAgreement(originalAgreement, validEntityAgreement);

      const authResponse = await service.acceptEntityAgreement(finalAgreement);

      session.initializeFromAuthResponse(authResponse);

      navigate("/dashboard", { replace: true });
    } catch (error: unknown) {
      if (axios.isCancel(error)) {
        navigate("/dashboard");
        return;
      }

      console.error(error);

      let message = "Couldn't accept agreement. Please try again";
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        message = String(error.response?.data);
      }

      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  const validEntityAgreement = (() => {
    if (!originalAgreement || !formEntityName || !formLocation) {
      return undefined;
    }
    try {
      return new EntityAgreement({
        id: originalAgreement.id,
        entityLocation: formLocation,
        entityName: formEntityName,
      });
    } catch {
      return undefined;
    }
  })();

  if (!formEntityName || !originalAgreement) return null;

  return (
    <PageContainer>
      <Stack>
        <Grid component="form">
          <Title variant="h4">Verify Entity Information</Title>
          <EntityNameInput
            label="Organization Name"
            initialValue={formEntityName}
            variant="outlined"
            onChange={(updatedEntityName?: EntityName) => {
              setFormEntityName(updatedEntityName);
            }}
          />
        </Grid>
        <Grid component="form">
          <LegalEntityLocationInput
            label="Location"
            variant="outlined"
            required
            value={formLocation ?? null}
            locationServicesDisabledRef={locationServicesRef}
            onLocationServicesDisabledChange={(newValue) => {
              locationServicesRef.current = newValue;
            }}
            onChange={(address: Address | null) => {
              setFormLocation(address ?? undefined);
            }}
          />
        </Grid>
        <Grid component="form">
          <AcceptButton
            id="entity-agreement-submit-button"
            variant="contained"
            color="primary"
            loading={saving}
            disabled={saving || !validEntityAgreement}
            startIcon={<CheckIcon />}
            onClick={handleAcceptAgreement}
          >
            Accept Agreement
          </AcceptButton>
        </Grid>
        <Grid>
          <HelperText>
            <Typography>By clicking the accept button above you agree to be bound by the following terms:</Typography>
          </HelperText>
        </Grid>
        <Grid>
          <article style={{ maxWidth: 800, margin: "0 auto" }}>
            <h3>Terms of Use</h3>
            <section style={{ textAlign: "left" }}>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et
                dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip
                ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu
                fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia
                deserunt mollit anim id est laborum.
              </p>
              <p>
                Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam
                rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt
                explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia
                consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui
                dolorem ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora
                incidunt ut labore et dolore magnam aliquam quaerat voluptatem. Ut enim ad minima veniam, quis nostrum
                exercitationem ullam corporis suscipit laboriosam, nisi ut aliquid ex ea commodi consequatur? Quis autem
                vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum
                qui dolorem eum fugiat quo voluptas nulla pariatur?
              </p>
              <p>
                At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum
                deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non
                provident, similique sunt in culpa qui officia deserunt mollitia animi, id est laborum et dolorum fuga.
                Et harum quidem rerum facilis est et expedita distinctio. Nam libero tempore, cum soluta nobis est
                eligendi optio cumque nihil impedit quo minus id quod maxime placeat facere possimus, omnis voluptas
                assumenda est, omnis dolor repellendus. Temporibus autem quibusdam et aut officiis debitis aut rerum
                necessitatibus saepe eveniet ut et voluptates repudiandae sint et molestiae non recusandae. Itaque earum
                rerum hic tenetur a sapiente delectus, ut aut reiciendis voluptatibus maiores alias consequatur aut
                perferendis doloribus asperiores repellat.
              </p>
              <p>
                Etiam sed ipsum vitae diam ultricies maximus ac vitae orci. Donec massa mi, iaculis eget vulputate ac,
                molestie sed diam. Ut in venenatis arcu, hendrerit imperdiet augue. Aliquam erat volutpat. Nam erat
                odio, mollis sit amet hendrerit blandit, lacinia sit amet nulla. Pellentesque non erat non ante rhoncus
                pulvinar pellentesque ac nunc. Cras a tortor at erat euismod suscipit id hendrerit lacus. Aenean in
                magna feugiat, euismod est sit amet, hendrerit risus. Fusce facilisis, ligula sit amet auctor fringilla,
                eros ante accumsan ligula, eget fermentum diam ante facilisis ante. Curabitur dapibus tortor non urna
                lacinia dapibus. Integer vitae justo ultrices, porttitor lorem et, vehicula velit. Nam ultricies dolor
                in sapien viverra aliquam.
              </p>
              <p>
                Morbi pharetra sed libero sit amet aliquet. Phasellus vel nisl ut felis dictum placerat accumsan sit
                amet massa. Sed vulputate commodo cursus. Suspendisse eget blandit odio. Nulla lobortis vel erat at
                imperdiet. Proin venenatis dolor vitae ultrices ullamcorper. Aenean eu nisi vel purus auctor mollis.
                Vivamus dapibus turpis quam, vitae volutpat metus vehicula eget. Vivamus dui eros, consequat sed ex id,
                congue consectetur metus. Donec lacinia tortor libero, id sodales erat finibus in. In libero lectus,
                commodo in urna euismod, auctor ultricies sem. Nunc tristique sem in enim suscipit hendrerit. Nullam nec
                mauris nec dui dictum molestie ut a nisl. Vivamus non egestas dui. Vivamus vulputate, velit eu
                pellentesque bibendum, odio quam finibus nibh, nec rhoncus erat tortor at neque. Sed sagittis tristique
                est eu consequat.
              </p>
              <p>
                Sed non odio eget ipsum pellentesque elementum. Vestibulum ante ipsum primis in faucibus orci luctus et
                ultrices posuere cubilia curae; Etiam sodales venenatis consequat. Vestibulum tincidunt, ante id
                hendrerit sollicitudin, orci neque convallis ipsum, eu interdum orci elit sit amet enim. Proin diam
                purus, blandit vitae nibh nec, cursus lacinia mi. Praesent lacinia volutpat urna sit amet consequat.
                Pellentesque blandit egestas finibus. Donec quis odio ut enim commodo lobortis. Sed non iaculis turpis.
                Sed luctus pulvinar metus nec ornare. Curabitur tincidunt laoreet nibh quis eleifend. Aliquam eu dui ex.
              </p>
            </section>
          </article>
        </Grid>
      </Stack>
    </PageContainer>
  );
}
